/**
 * designConstants.ts - Design System Constants and Templates
 *
 * This file contains all the design system constants used in the fundraiser builder,
 * including layout templates, color schemes, and available sections for drag-and-drop.
 *
 * Key Features:
 * - Layout templates with zone configurations (5 different layouts)
 * - Color templates with predefined color schemes
 * - Legacy color swatch support for backward compatibility
 * - Available sections list for drag-and-drop interface
 * - Zone width calculations and default section assignments
 *
 * Layout System:
 * - Layout 1: Full + 2/3 + 1/3 + Full (default)
 * - Layout 2: Full + 1/3 + 2/3 + Full (reversed columns)
 * - Layout 3: Full + 1/2 + 1/2 + Full (equal columns)
 * - Layout 4: 2/3 + 1/3 + Full (no top zone)
 * - Layout 5: Full Width (single column)
 *
 * Dependencies: None (pure constants and type definitions)
 */

import { ColorSwatches, FundRaiserTemplateLayoutType } from "../../../enums";
import Layout1Icon from "../../../../../../images/fundraiser/Layout 1.svg";
import Layout2Icon from "../../../../../../images/fundraiser/Layout 2.svg";
import Layout3Icon from "../../../../../../images/fundraiser/Layout 3.svg";
import Layout4Icon from "../../../../../../images/fundraiser/Layout 4.svg";
import Layout5Icon from "../../../../../../images/fundraiser/Layout 5.svg";
import { ColorModel, RGBA } from "../../../models";
// Constants for design step layouts, colors, and sections

export const layoutTemplates = [
  {
    id: FundRaiserTemplateLayoutType.Layout1,
    name: "Layout 1",
    description: "Full + 2/3 + 1/3 + Full",
    image: Layout1Icon,
    zones: {
      topZone: 100,
      leftZone: 67,
      rightZone: 33,
      bottomZone: 100,
    },
    defaultSections: {
      topZone: ["mission", "impact", "progress"],
      leftZone: ["media", "stats", "donation"],
      rightZone: ["cta", "promoters", "recent"],
      bottomZone: [],
    },
  },
  {
    id: FundRaiserTemplateLayoutType.Layout2,
    name: "Layout 2",
    description: "Full + 1/3 + 2/3 + Full",
    image: Layout2Icon,
    zones: {
      topZone: 100,
      leftZone: 33,
      rightZone: 67,
      bottomZone: 100,
    },
    defaultSections: {
      topZone: ["mission", "impact", "progress"],
      leftZone: ["cta", "promoters", "recent"],
      rightZone: ["media", "stats", "donation"],
      bottomZone: [],
    },
  },
  {
    id: FundRaiserTemplateLayoutType.Layout3,
    name: "Layout 3",
    description: "Full + 1/2 + 1/2 + Full",
    image: Layout3Icon,
    zones: {
      topZone: 100,
      leftZone: 50,
      rightZone: 50,
      bottomZone: 100,
    },
    defaultSections: {
      topZone: ["mission", "impact", "progress"],
      leftZone: ["media", "stats", "donation"],
      rightZone: ["cta", "promoters", "recent"],
      bottomZone: [],
    },
  },
  {
    id: FundRaiserTemplateLayoutType.Layout4,
    name: "Layout 4",
    description: "2/3 + 1/3 + Full",
    image: Layout4Icon,
    zones: {
      topZone: 0,
      leftZone: 67,
      rightZone: 33,
      bottomZone: 100,
    },
    defaultSections: {
      topZone: [],
      leftZone: ["mission", "media", "progress", "stats", "donation"],
      rightZone: ["impact", "cta", "promoters", "recent"],
      bottomZone: [],
    },
  },
  {
    id: FundRaiserTemplateLayoutType.Layout5,
    name: "Layout 5",
    description: "Full Width",
    image: Layout5Icon,
    zones: {
      topZone: 100,
      leftZone: 0,
      rightZone: 0,
      bottomZone: 0,
    },
    defaultSections: {
      topZone: [
        "mission",
        "impact",
        "media",
        "cta",
        "progress",
        "donation",
        "stats",
        "promoters",
        "recent",
      ],
      leftZone: [],
      rightZone: [],
      bottomZone: [],
    },
  },
];

// Color Template Interface
export interface ColorTemplate {
  id: string;
  name: string;
  colors: {
    background: ColorModel;
    primary: ColorModel;
    secondary: ColorModel;
    accent: ColorModel;
    text: ColorModel;
  };
  swatches: string[]; // Array of 4-5 color values for display
}

export const colorTemplates: ColorTemplate[] = [
  {
    id: ColorSwatches.Default,
    name: "Default",
    colors: {
      background: new ColorModel({
        hex: "#FFFFFF",
        rgba: "rgba(255, 255, 255, 1)",
        rgbValue: new RGBA({ r: "255", g: "255", b: "255", a: "1" }),
      }),
      primary: new ColorModel({
        hex: "#12B981",
        rgba: "rgba(18, 185, 129, 1)",
        rgbValue: new RGBA({ r: "18", g: "185", b: "129", a: "1" }),
      }),
      secondary: new ColorModel({
        hex: "#3986EF",
        rgba: "rgba(57, 134, 239, 1)",
        rgbValue: new RGBA({ r: "57", g: "134", b: "239", a: "1" }),
      }),
      accent: new ColorModel({
        hex: "#ECFFFC",
        rgba: "rgba(236, 255, 252, 1)",
        rgbValue: new RGBA({ r: "236", g: "255", b: "252", a: "1" }),
      }),
      text: new ColorModel({
        hex: "#1E293B",
        rgba: "rgb(30, 41, 59)",
        rgbValue: new RGBA({ r: "30", g: "41", b: "59", a: "1" }),
      }),
    },
    swatches: ["#FFFFFF", "#12B981", "#3986EF", "#ECFFFC", "#1E293B"],
  },
  {
    id: ColorSwatches.Ocean,
    name: "Ocean",
    colors: {
      background: new ColorModel({
        hex: "#E8F9FB",
        rgba: "rgba(232, 249, 251, 1)",
        rgbValue: new RGBA({ r: "232", g: "249", b: "251", a: "1" }),
      }),
      primary: new ColorModel({
        hex: "#0EA5E9",
        rgba: "rgba(14, 165, 233, 1)",
        rgbValue: new RGBA({ r: "14", g: "165", b: "233", a: "1" }),
      }),
      secondary: new ColorModel({
        hex: "#06B6D4",
        rgba: "rgba(6, 182, 212, 1)",
        rgbValue: new RGBA({ r: "6", g: "182", b: "212", a: "1" }),
      }),
      accent: new ColorModel({
        hex: "#D1ECFA",
        rgba: "rgba(209, 236, 250, 1)",
        rgbValue: new RGBA({ r: "209", g: "236", b: "250", a: "1" }),
      }),
      text: new ColorModel({
        hex: "#1E293B",
        rgba: "rgb(30, 41, 59)",
        rgbValue: new RGBA({ r: "30", g: "41", b: "59", a: "1" }),
      }),
    },
    swatches: ["#E8F9FB", "#0EA5E9", "#06B6D4", "#D1ECFA", "#1E293B"],
  },
  {
    id: ColorSwatches.Forest,
    name: "Forest",
    colors: {
      background: new ColorModel({
        hex: "#F4FEF6",
        rgba: "rgba(244, 254, 246, 1)",
        rgbValue: new RGBA({ r: "244", g: "254", b: "246", a: "1" }),
      }),
      primary: new ColorModel({
        hex: "#10B981",
        rgba: "rgba(16, 185, 129, 1)",
        rgbValue: new RGBA({ r: "16", g: "185", b: "129", a: "1" }),
      }),
      secondary: new ColorModel({
        hex: "#059669",
        rgba: "rgba(5, 150, 105, 1)",
        rgbValue: new RGBA({ r: "5", g: "150", b: "105", a: "1" }),
      }),
      accent: new ColorModel({
        hex: "#C6F6D5",
        rgba: "rgba(198, 246, 213, 1)",
        rgbValue: new RGBA({ r: "198", g: "246", b: "213", a: "1" }),
      }),
      text: new ColorModel({
        hex: "#1E293B",
        rgba: "rgb(30, 41, 59)",
        rgbValue: new RGBA({ r: "30", g: "41", b: "59", a: "1" }),
      }),
    },
    swatches: ["#F4FEF6", "#10B981", "#059669", "#C6F6D5", "#1E293B"],
  },
  {
    id: ColorSwatches.Sunset,
    name: "Sunset",
    colors: {
      background: new ColorModel({
        hex: "#FFFAF0",
        rgba: "rgba(255, 250, 240, 1)",
        rgbValue: new RGBA({ r: "255", g: "250", b: "240", a: "1" }),
      }),
      primary: new ColorModel({
        hex: "#F97316",
        rgba: "rgba(249, 115, 22, 1)",
        rgbValue: new RGBA({ r: "249", g: "115", b: "22", a: "1" }),
      }),
      secondary: new ColorModel({
        hex: "#EAB308",
        rgba: "rgba(234, 179, 8, 1)",
        rgbValue: new RGBA({ r: "234", g: "179", b: "8", a: "1" }),
      }),
      accent: new ColorModel({
        hex: "#FEE2A3",
        rgba: "rgba(254, 226, 163, 1)",
        rgbValue: new RGBA({ r: "254", g: "226", b: "163", a: "1" }),
      }),
      text: new ColorModel({
        hex: "#922C0E",
        rgba: "rgb(146, 64, 14)",
        rgbValue: new RGBA({ r: "146", g: "64", b: "14", a: "1" }),
      }),
    },
    swatches: ["#FFFAF0", "#F97316", "#EAB308", "#FEE2A3", "#922C0E"],
  },
  {
    id: ColorSwatches.Purple,
    name: "Purple",
    colors: {
      background: new ColorModel({
        hex: "#FCF8FF",
        rgba: "rgba(252, 248, 255, 1)",
        rgbValue: new RGBA({ r: "252", g: "248", b: "255", a: "1" }),
      }),
      primary: new ColorModel({
        hex: "#8B5CF6",
        rgba: "rgba(139, 92, 246, 1)",
        rgbValue: new RGBA({ r: "139", g: "92", b: "246", a: "1" }),
      }),
      secondary: new ColorModel({
        hex: "#A855F7",
        rgba: "rgba(168, 85, 247, 1)",
        rgbValue: new RGBA({ r: "168", g: "85", b: "247", a: "1" }),
      }),
      accent: new ColorModel({
        hex: "#E0E7FF",
        rgba: "rgba(224, 231, 255, 1)",
        rgbValue: new RGBA({ r: "224", g: "231", b: "255", a: "1" }),
      }),
      text: new ColorModel({
        hex: "#1E293B",
        rgba: "rgb(30, 41, 59)",
        rgbValue: new RGBA({ r: "30", g: "41", b: "59", a: "1" }),
      }),
    },
    swatches: ["#FCF8FF", "#8B5CF6", "#A855F7", "#E0E7FF", "#1E293B"],
  },
  {
    id: ColorSwatches.Pink,
    name: "Pink",
    colors: {
      background: new ColorModel({
        hex: "#FEF5F9",
        rgba: "rgba(254, 245, 249, 1)",
        rgbValue: new RGBA({ r: "254", g: "245", b: "249", a: "1" }),
      }),
      primary: new ColorModel({
        hex: "#EC4899",
        rgba: "rgba(236, 72, 153, 1)",
        rgbValue: new RGBA({ r: "236", g: "72", b: "153", a: "1" }),
      }),
      secondary: new ColorModel({
        hex: "#F43F5E",
        rgba: "rgba(244, 63, 94, 1)",
        rgbValue: new RGBA({ r: "244", g: "63", b: "94", a: "1" }),
      }),
      accent: new ColorModel({
        hex: "#FBDAE8",
        rgba: "rgba(251, 218, 232, 1)",
        rgbValue: new RGBA({ r: "251", g: "218", b: "232", a: "1" }),
      }),
      text: new ColorModel({
        hex: "#832643",
        rgba: "rgb(131, 24, 67)",
        rgbValue: new RGBA({ r: "131", g: "24", b: "67", a: "1" }),
      }),
    },
    swatches: ["#FEF5F9", "#EC4899", "#F43F5E", "#FBDAE8", "#832643"],
  },
];

// Legacy color swatches - kept for backward compatibility
export const colorSwatches = [
  {
    id: "swatch1",
    primary: "#12B981",
    secondary: "#3986EF",
    accent: "#ECFFFC",
    text: "#1e293b",
    background: "#FFFFFF",
  },
  {
    id: "swatch3",
    primary: "#3468CB",
    secondary: "#CB9734",
    accent: "#E1EBFF",
    text: "#1E293B",
    background: "#F8F8F8",
  },
  {
    id: "swatch5",
    primary: "#8745BA",
    secondary: "#78BA45",
    accent: "#F0DCFF",
    text: "#1E293B",
    background: "#FFFFFF",
  },
  {
    id: "swatch7",
    primary: "#C23D7F",
    secondary: "#3DC280",
    accent: "#FFD7EB",
    text: "#1E293B",
    background: "#F8F8F8",
  },
];

// Available sections arranged for 2-column layout
export const availableSections = [
  { id: "cta", name: "Call to Action" },
  { id: "description", name: "Description" },
  { id: "recent", name: "Donations" },
  { id: "donation", name: "Donation Form" },
  { id: "impact", name: "Impact" },
  { id: "promoters", name: "Leaderboard" },
  { id: "media", name: "Media" },
  { id: "mission", name: "Mission" },
  { id: "progress", name: "Progress" },
  { id: "sponsor", name: "Sponsor" },
  { id: "stats", name: "Stats" },
].sort((a, b) => a.name.localeCompare(b.name));
